import { useMemo } from "react";
import { Brain, TrendingUp, TrendingDown, Minus, ShieldAlert, Zap, RefreshCw, EyeOff, X } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/* INTELLIGENCE DESK panel — fed by the WS snapshot (state.ai). No polling. */

const dirMeta = (d) =>
  d === "bullish"
    ? { col: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/40", Icon: TrendingUp, label: "BULLISH" }
    : d === "bearish"
    ? { col: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/40", Icon: TrendingDown, label: "BEARISH" }
    : { col: "text-zinc-400", bg: "bg-zinc-500/10", border: "border-zinc-700", Icon: Minus, label: "NEUTRAL" };

const AGENT_LABEL = {
  market_structure_agent: "STRUCTURE",
  momentum_agent: "MOMENTUM",
  trade_management_agent: "TRADE MGMT",
  liquidity_agent: "LIQUIDITY",
  macro_event_agent: "MACRO",
  htf: "HIGHER TF",
};

const heldFor = (ts) => {
  if (!ts) return "—";
  const s = Math.max(0, Math.round(Date.now() / 1000 - ts));
  return s < 60 ? `${s}s` : s < 3600 ? `${Math.round(s / 60)}m` : `${(s / 3600).toFixed(1)}h`;
};

const pct = (v, suffix = "%") => (v == null ? "—" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}${suffix}`);

const ago = (ts) => {
  if (!ts) return "—";
  const s = Math.max(0, Math.round(Date.now() / 1000 - ts));
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)}m ago` : `${Math.round(s / 3600)}h ago`;
};

const DeskPosture = ({ tr, od, skip }) => {
  const barsLeft = od ? Math.max(0, Math.round(od.ttl_bars - (Date.now() / 1000 - od.placed_ts) / od.bar_secs)) : 0;
  const cancelOrder = async () => {
    try { await api.deskCancel(od.id); toast.success("Desk limit cancelled"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  if (!tr && !od && !skip) return null;
  return (
    <>
      {/* always-on trend tracker */}
      {tr && (
        <div className="rounded-md border border-white/[0.08] bg-white/[0.02] px-2.5 py-2" data-testid="desk-trend">
          <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-[0.18em]">
            <span className="text-zinc-500">Trend tracker</span>
            <span className="text-zinc-600">{tr.bias_source ? `via ${tr.bias_source}` : ""}</span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className={`text-[11px] font-bold font-mono ${dirMeta(tr.bias).col}`} data-testid="desk-trend-bias">
              {tr.bias ? tr.bias.toUpperCase() : "FLAT"} · {String(tr.leg || "").replace("_", " ").toUpperCase()}
            </span>
            <span className="text-[9px] font-mono text-zinc-400" data-testid="desk-trend-pullback">
              {tr.pullback_pct != null ? `pullback ${tr.pullback_pct}%` : "—"}
            </span>
          </div>
          <div className="mt-0.5 text-[8.5px] font-mono text-zinc-600 truncate">
            {tr.entry_targets?.length ? `next ${tr.entry_targets[0].type === "fvg" ? "FVG" : "OB"} ${tr.entry_targets[0].level} (${tr.entry_targets[0].distance_atr} ATR)` : "no FVG / OB in reach"}
            {tr.extension_targets?.length ? ` · ext ${tr.extension_targets[0].level}` : ""}
            {tr.reversal_levels?.length ? ` · sweep ${tr.reversal_levels[0].level} → ${tr.reversal_levels[0].reversal_bias}` : ""}
          </div>
        </div>
      )}

      {/* resting desk limit */}
      {od ? (
        <div className={`rounded-md border px-2.5 py-2 ${od.direction === "LONG" ? "border-emerald-400/30 bg-emerald-500/[0.05]" : "border-red-400/30 bg-red-500/[0.05]"}`} data-testid="desk-order">
          <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-[0.18em]">
            <span className="text-zinc-500">Desk limit · {od.mode}</span>
            <button onClick={cancelOrder} className="flex items-center gap-1 text-zinc-500 hover:text-red-300" data-testid="desk-order-cancel">
              <X className="h-3 w-3" /> cancel
            </button>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className={`text-[11px] font-bold font-mono ${od.direction === "LONG" ? "text-emerald-400" : "text-red-400"}`} data-testid="desk-order-summary">
              {od.direction} {od.qty}x @ {od.entry}
            </span>
            <span className="text-[9px] font-mono text-zinc-400">{od.kind} · {od.rr}R · {barsLeft} bars left</span>
          </div>
          <div className="mt-0.5 text-[8.5px] font-mono text-zinc-600">SL {od.stop_loss} · TP {od.take_profit} ({od.basis?.tp_basis}) · {Math.round(od.confidence)}% desk</div>
        </div>
      ) : skip ? (
        <div className="text-[8.5px] font-mono text-zinc-600 px-0.5" data-testid="desk-order-skip">Desk limit: {skip}</div>
      ) : null}

    </>
  );
};

export const AiIntelligence = ({ ai, symbol, onAnalyze }) => {
  const c = ai?.consensus;
  const p = ai?.prognosis && ai.prognosis.symbol === symbol ? ai.prognosis : null;
  const xm = p?.cross_market;
  const tr = ai?.trend && ai.trend.symbol === symbol ? ai.trend : null;
  const od = ai?.desk_order && ai.desk_order.symbol === symbol ? ai.desk_order : null;
  const meta = dirMeta(c?.direction);
  const specialists = useMemo(() => Object.entries(c?.per_agent || {}), [c]);

  if (!ai?.enabled) {
    return (
      <div className="h-full grid place-items-center text-center px-3" data-testid="ai-panel-disabled">
        <div className="space-y-1.5">
          <EyeOff size={16} className="mx-auto text-zinc-600" />
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-500">Intelligence desk off</div>
          <div className="text-[9px] text-zinc-600 leading-relaxed">Set MOONSHOT_API_KEY to bring the agent desk online</div>
        </div>
      </div>
    );
  }

  if (!c) {
    return (
      <div className="h-full overflow-y-auto pr-0.5 space-y-2.5" data-testid="ai-panel-waiting">
        <DeskPosture tr={tr} od={od} skip={ai?.desk_skip} />
        <div className="space-y-1.5 text-center px-3 py-3">
          <Brain size={16} className="mx-auto text-blue-400 animate-pulse" />
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-zinc-400">
            {ai.unavailable_reason ? "Desk temporarily unavailable" : "Awaiting first consensus"}
          </div>
          <div className="text-[9px] text-zinc-600 leading-relaxed max-w-[220px]">
            {ai.unavailable_reason || `Agents analyse ${symbol} on the next structure, target or candle event`}
          </div>
          {onAnalyze && (
            <button data-testid="ai-analyze-btn" onClick={onAnalyze}
              className="mt-1 inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors">
              <RefreshCw size={9} /> analyze now
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto pr-0.5 space-y-2.5" data-testid="ai-panel">
      {/* bias + confidence */}
      <div className={`rounded-md border ${meta.border} ${meta.bg} p-2.5`} data-testid="ai-bias">
        <div className="flex items-center justify-between">
          <span className={`flex items-center gap-1.5 text-sm font-black tracking-[0.12em] ${meta.col}`}>
            <meta.Icon size={14} /> {meta.label}
          </span>
          <span className={`text-xl font-black font-mono tabular-nums ${meta.col}`} data-testid="ai-confidence">
            {Math.round(c.confidence)}%
          </span>
        </div>
        <div className="mt-1.5 h-[3px] w-full rounded-full bg-white/[0.07] overflow-hidden">
          <div className="h-full rounded-full transition-[width] duration-700"
            style={{ width: `${Math.max(2, c.confidence)}%`, background: "currentColor" }} />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-[9px] font-mono text-zinc-500">
          <span data-testid="ai-agreement">{String(c.agreement || "").replace(/_/g, " ")} agents aligned</span>
          {c.confidence_change != null && (
            <span className={c.confidence_change >= 0 ? "text-emerald-400" : "text-red-400"}>
              {c.confidence_change >= 0 ? "+" : ""}{c.confidence_change}
            </span>
          )}
        </div>
        {c.partial && (
          <div className="mt-1 text-[8.5px] font-mono uppercase tracking-[0.14em] text-amber-400">{c.label}</div>
        )}
      </div>

      <DeskPosture tr={tr} od={od} skip={ai?.desk_skip} />

      {/* persistent call on the chart */}
      {p && (
        <div className="rounded-md border border-white/[0.08] bg-white/[0.02] px-2.5 py-2" data-testid="desk-prognosis">
          <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-[0.18em]">
            <span className="text-zinc-500">Call on chart</span>
            <span className={p.status === "invalidated" ? "text-red-400" : "text-cyan-300"} data-testid="desk-prognosis-status">
              {p.status === "invalidated" ? "INVALIDATED" : "HELD"}
            </span>
          </div>
          <div className="mt-1 flex items-baseline justify-between">
            <span className={`text-[11px] font-bold font-mono ${dirMeta(p.direction).col}`}>
              {String(p.direction || "").toUpperCase()} {Math.round(p.confidence || 0)}% @ {p.price_at}
            </span>
            <span className="text-[9px] font-mono text-zinc-400" data-testid="desk-prognosis-held">
              held {heldFor(p.anchor_ts)} · {p.adjustments_today || 0} adj today
            </span>
          </div>
          <div className="mt-0.5 text-[8.5px] font-mono text-zinc-600 truncate">
            {p.adjust_reason}{p.invalidation != null ? ` · invalidation ${p.invalidation}` : ""}{p.target != null ? ` · target ${p.target}` : ""}
          </div>
        </div>
      )}

      {/* liquidity marks */}
      {!!(p?.liquidity_targets || []).length && (
        <div data-testid="desk-liquidity">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Liquidity</div>
          <ul className="space-y-0.5">
            {p.liquidity_targets.slice(0, 5).map((t, i) => (
              <li key={i} className="flex items-center gap-1.5 text-[9.5px] font-mono">
                <span className={t.status === "swept" ? "text-yellow-400" : "text-cyan-300"}>
                  {t.status === "swept" ? "SWEPT" : "NEXT"}
                </span>
                <span className="text-zinc-200 tabular-nums">{t.level}</span>
                <span className="text-zinc-500">{t.side}</span>
                {t.reversal_bias && <span className={dirMeta(t.reversal_bias).col}>rev {t.reversal_bias}</span>}
                {t.note && <span className="text-zinc-600 truncate">{t.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* VIX + Mag 7 confluence for index futures */}
      {xm && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 rounded-md border border-white/[0.06] px-2.5 py-1.5" data-testid="desk-cross-market">
          <Kv k="VIX" v={xm.vix?.level != null ? `${xm.vix.level} (${pct(xm.vix.change_1h, "")} 1h) ${String(xm.vix.regime || "").toUpperCase()}` : "— feed pending"}
            col={xm.vix?.rising ? "text-amber-400" : "text-zinc-200"} testid="desk-vix" />
          <Kv k="Mag 7" v={xm.mag7?.count ? `${xm.mag7.bias} ${xm.mag7.up}/${xm.mag7.count} up · ${pct(xm.mag7.avg_change_pct)}` : "— feed pending"}
            col={xm.mag7?.bias === "BULLISH" ? "text-emerald-400" : xm.mag7?.bias === "BEARISH" ? "text-red-400" : "text-zinc-200"} testid="desk-mag7" />
          {!!(xm.mag7?.leaders || []).length && (
            <div className="col-span-2 text-[8.5px] font-mono text-zinc-600 truncate">
              lead {xm.mag7.leaders.join(", ")} · lag {(xm.mag7.laggards || []).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* what changed */}
      {!!(c.what_changed || []).length && (
        <div data-testid="ai-what-changed">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">What changed</div>
          <ul className="space-y-0.5">
            {c.what_changed.slice(0, 4).map((x, i) => (
              <li key={i} className="text-[10px] leading-snug text-zinc-300 font-mono">{x}</li>
            ))}
          </ul>
        </div>
      )}

      {/* specialist votes */}
      <div data-testid="ai-specialists">
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Specialists</div>
        <div className="space-y-1">
          {specialists.map(([name, v]) => {
            const m = dirMeta(v.bias);
            return (
              <div key={name} className="flex items-center gap-2" data-testid={`ai-agent-${name}`}>
                <span className="w-[68px] shrink-0 text-[8.5px] font-mono uppercase tracking-[0.1em] text-zinc-500">
                  {AGENT_LABEL[name] || name.replace(/_/g, " ")}
                </span>
                <div className="flex-1 h-[3px] rounded-full bg-white/[0.06] overflow-hidden">
                  <div className="h-full rounded-full" style={{
                    width: `${Math.max(3, v.confidence)}%`,
                    background: v.bias === "bullish" ? "#34d399" : v.bias === "bearish" ? "#f87171" : "#71717a",
                  }} />
                </div>
                <span className={`w-9 text-right text-[9px] font-mono tabular-nums ${m.col}`}>{Math.round(v.confidence)}%</span>
                <span className="w-6 text-[8px] font-mono text-zinc-600">×{v.weight}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* supporting / opposing */}
      <div className="grid grid-cols-1 gap-2">
        {!!(c.supporting_factors || []).length && (
          <div data-testid="ai-supporting">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-emerald-500/80 mb-1">Supporting</div>
            {c.supporting_factors.slice(0, 3).map((s, i) => (
              <div key={i} className="text-[10px] leading-snug text-zinc-300">+ {s}</div>
            ))}
          </div>
        )}
        {!!(c.opposing_factors || []).length && (
          <div data-testid="ai-opposing">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-red-500/80 mb-1">Opposing</div>
            {c.opposing_factors.slice(0, 3).map((s, i) => (
              <div key={i} className="text-[10px] leading-snug text-zinc-400">− {s}</div>
            ))}
          </div>
        )}
      </div>

      {/* invalidation / regime / risk / targets */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-white/[0.06] pt-2">
        <Kv k="Invalidation" v={c.invalidation ?? "—"} testid="ai-invalidation" />
        <Kv k="Regime" v={(c.market_regime || "—").toUpperCase()} />
        <Kv k="Risk state" v={(c.risk_state || "—").toUpperCase()}
          col={c.risk_state === "normal" ? "text-zinc-200" : "text-amber-400"} testid="ai-risk-state" />
        <Kv k="Target" v={c.target_outlook?.tp1 || c.target_outlook?.note || "—"} testid="ai-target-outlook" />
      </div>

      {!!(ai.notifications || []).length && (
        <div data-testid="ai-alerts">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Desk alerts</div>
          {ai.notifications.slice(0, 4).map((n) => (
            <div key={n.id} className="flex items-center gap-1.5 text-[9.5px] font-mono">
              {n.level === "alert" ? <ShieldAlert size={9} className="text-red-400" /> : <Zap size={9} className="text-amber-400" />}
              <span className="text-zinc-300">{n.title}</span>
              <span className="text-zinc-600">{n.detail}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-white/[0.06] pt-2 text-[8.5px] font-mono uppercase tracking-[0.12em] text-zinc-600">
        <span data-testid="ai-last-analysis">Last {ago(ai.last_analysis_at)}</span>
        <span>{ai.shadow_mode ? "SHADOW" : "LIVE"} · {ai.model}</span>
      </div>
      <div className="text-[8.5px] font-mono text-zinc-600" data-testid="ai-next-trigger">
        Next trigger: {c.next_trigger || "structure or target change"}
      </div>
      {onAnalyze && (
        <button data-testid="ai-analyze-btn" onClick={onAnalyze}
          className="w-full inline-flex items-center justify-center gap-1 rounded border border-zinc-700 px-2 py-1 text-[9px] font-mono uppercase tracking-[0.14em] text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors">
          <RefreshCw size={9} /> re-analyze {symbol}
        </button>
      )}
    </div>
  );
};

const Kv = ({ k, v, col = "text-zinc-200", testid }) => (
  <div className="min-w-0">
    <div className="text-[8px] font-mono uppercase tracking-[0.14em] text-zinc-600">{k}</div>
    <div className={`text-[10.5px] font-mono truncate ${col}`} data-testid={testid}>{String(v)}</div>
  </div>
);
